"use client";

import { ArrowUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const CIRCUMFERENCE = 2 * Math.PI * 18;

export function ScrollToTop() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const circleRef = useRef<SVGCircleElement>(null);

  const showProgress =
    pathname.startsWith("/guides/") ||
    pathname.startsWith("/cheat-sheets/apa") ||
    pathname.startsWith("/cheat-sheets/mla") ||
    pathname.startsWith("/cheat-sheets/chicago");

  useEffect(() => {
    let rafId = 0;

    function update() {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      setVisible(scrollTop > 400);
      if (showProgress && docHeight > 0 && circleRef.current) {
        const progress = Math.min(scrollTop / docHeight, 1);
        circleRef.current.style.strokeDashoffset = String(
          CIRCUMFERENCE * (1 - progress)
        );
      }
    }

    function handleScroll() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    update();
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showProgress]);

  if (!visible) {
    return null;
  }

  return (
    <button
      aria-label="Scroll to top"
      className="fixed right-6 bottom-6 z-40 flex size-11 items-center justify-center rounded-full border border-border bg-background shadow-lg transition-all hover:bg-muted"
      onClick={() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      type="button"
    >
      {showProgress && (
        <svg
          aria-hidden="true"
          className="absolute inset-0 -rotate-90"
          height="44"
          viewBox="0 0 44 44"
          width="44"
        >
          <circle
            className="text-primary/30"
            cx="22"
            cy="22"
            fill="none"
            r="18"
            stroke="currentColor"
            strokeWidth="2"
          />
          <circle
            className="text-primary"
            cx="22"
            cy="22"
            fill="none"
            r="18"
            ref={circleRef}
            stroke="currentColor"
            strokeDasharray={CIRCUMFERENCE}
            strokeLinecap="round"
            strokeWidth="2"
            style={{
              strokeDashoffset: CIRCUMFERENCE,
              willChange: "stroke-dashoffset",
            }}
          />
        </svg>
      )}
      <ArrowUp className="relative size-4" />
    </button>
  );
}
